/**
 * Display helpers for the "Can you make it?" answers (eventAttendance docs).
 *
 * A doc exists only for members the day-before question went to. Its `status`
 * is "asked" until they answer and "answered" after, with `answer` one of
 * yes / no / maybe and `reply` what they texted (null when they tapped a
 * button). Two things sit on top of that:
 *
 *  - `noConfirmation.status === "pending"`: they said no, but they paid and
 *    tickets are not refunded, so we asked them to confirm. Until they do, the
 *    no is not recorded and must not read as decided.
 *  - after the event, `outcome` ("showed" | "no_show" | "off_list") says
 *    whether they checked in, which turns "Coming" into "Said yes, didn't show".
 */

export const ATTENDANCE_STATES = {
  yes: { label: "Coming", tone: "green" },
  no: { label: "Can't make it", tone: "red" },
  maybe: { label: "Maybe", tone: "amber" },
  asked: { label: "No reply yet", tone: "gray" },
  unconfirmedNo: { label: "Said no, not confirmed", tone: "amber" },
  // After the event, from the check-in list.
  yesNoShow: { label: "Said yes, didn't show", tone: "red" },
  noShowed: { label: "Said no, came", tone: "green" },
};

const BASE_STATES = ["yes", "no", "maybe"];

/** The state key for one member's doc, or null when they were never asked. */
export const attendanceState = (entry) => {
  if (!entry || typeof entry !== "object") return null;
  if (entry.noConfirmation?.status === "pending") return "unconfirmedNo";
  if (entry.status === "answered" && BASE_STATES.includes(entry.answer)) {
    if (entry.answer === "yes" && entry.outcome === "no_show") return "yesNoShow";
    if (entry.answer === "no" && entry.outcome === "showed") return "noShowed";
    return entry.answer;
  }
  if (entry.status === "asked") return "asked";
  return null;
};

/** Their NO gave their spot back, so they are off the guest list because they answered. */
export const isReleasedNo = (entry) =>
  !!entry && entry.seatReleased === true && entry.status === "answered" && entry.answer === "no";

/**
 * Counts across a userId -> doc map, in the order the summary line reads them.
 * Pass the docs of the guests ON the list: someone who answered and then
 * cancelled still has a doc, and counting it makes the headcount wrong.
 */
export const attendanceSummary = (byUser) => {
  const counts = { yes: 0, no: 0, maybe: 0, unconfirmedNo: 0, asked: 0 };
  Object.values(byUser || {}).forEach((entry) => {
    const state = attendanceState(entry);
    if (!state) return;
    // The summary is what they SAID; the after-event states fold back into it.
    if (state === "yesNoShow") counts.yes += 1;
    else if (state === "noShowed") counts.no += 1;
    else counts[state] += 1;
  });
  return { ...counts, total: counts.yes + counts.no + counts.maybe + counts.unconfirmedNo + counts.asked };
};

/** "12 coming, 3 can't make it, 1 maybe, 4 no reply yet", skipping zero counts. */
export const attendanceSummaryText = (summary) => {
  const parts = [];
  if (summary.yes) parts.push(`${summary.yes} coming`);
  if (summary.no) parts.push(`${summary.no} can't make it`);
  if (summary.unconfirmedNo) parts.push(`${summary.unconfirmedNo} said no, not confirmed`);
  if (summary.maybe) parts.push(`${summary.maybe} maybe`);
  if (summary.asked) parts.push(`${summary.asked} no reply yet`);
  return parts.join(", ");
};

/**
 * The after-event line from eventAttendanceSummaries/{eventId}.outcome:
 * "Of 12 who said yes, 10 showed up. 1 who said no came anyway."
 * Null until the event has been resolved, or when nobody said yes or no.
 */
export const attendanceOutcomeText = (outcome) => {
  if (!outcome || typeof outcome !== "object") return null;
  const parts = [];
  if (outcome.saidYes > 0) parts.push(`Of ${outcome.saidYes} who said yes, ${outcome.saidYesShowed || 0} showed up.`);
  if (outcome.saidNoShowed > 0) parts.push(`${outcome.saidNoShowed} who said no came anyway.`);
  if (outcome.noReplyShowed > 0) parts.push(`${outcome.noReplyShowed} who never replied came.`);
  return parts.length > 0 ? parts.join(" ") : null;
};

const VIA_TEXT = {
  sms: "by text",
  push: "from the notification",
  web: "on the event page",
  app: "in the app",
  assistant: "through Gist AI",
};

/** One plain sentence on how things stand, for the badge tooltip and the guest modal. */
export const attendanceDetailText = (entry) => {
  const state = attendanceState(entry);
  if (!state) return null;
  const askedHow = entry.askedVia === "push" ? "Asked with a notification" : "Asked by text";
  if (state === "asked") return `${askedHow}, no reply yet`;
  if (state === "unconfirmedNo") {
    return "Said no. They paid and tickets are not refunded, so we asked them to confirm. Nothing is recorded until they do.";
  }
  const how = VIA_TEXT[entry.via] ? `Answered ${VIA_TEXT[entry.via]}` : "Answered";
  const confirmed = entry.noConfirmation?.status === "confirmed"
    ? " Paid ticket: they confirmed after we told them it is not refunded."
    : "";
  // Only guests ON the list get a badge, so a released flag here is history:
  // the server clears it on their next answer, not when they sign up again.
  const released = entry.seatReleased ? " Their spot was given back earlier, and they have signed up again since." : "";
  return `${how}.${confirmed}${released}`;
};

/** When they answered, as a short local time, or null. */
export const answeredAtText = (entry) => {
  const at = entry?.answeredAt;
  const date = at?.toDate ? at.toDate() : at instanceof Date ? at : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

/** "Said yes 5 times, showed up 4." from a memberReliability doc, or null. */
export const reliabilityText = (reliability) => {
  const showed = Number(reliability?.saidYesShowed) || 0;
  const saidYes = showed + (Number(reliability?.saidYesNoShow) || 0);
  if (saidYes === 0) return null;
  return `Said yes ${saidYes} ${saidYes === 1 ? "time" : "times"}, showed up ${showed}.`;
};
